import React, { useEffect, useState } from 'react';
import './Table.css';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../utils/axiosInstance';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Spinner, Alert } from 'react-bootstrap';
import { formatISOToLocalTime, normalizeDbTimestampToUtcIso } from '../../utils/timeUtils';

const Table = ({ adminIds, empRole }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userTimeZone, setUserTimeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);

  useEffect(() => {
    fetchEntries(selectedDate);
    fetchEmployees();

    // Setup timezone detection
    const detectTimezone = () => {
      const detectedZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detectedZone !== userTimeZone) {
        setUserTimeZone(detectedZone);
      }
    };

    window.addEventListener('focus', detectTimezone);
    return () => {
      window.removeEventListener('focus', detectTimezone);
    };
  }, [selectedDate, userTimeZone]);

  useEffect(() => {
    const loggedInEmployeesCount = data.filter((attendance) => attendance.punch_out_time === null).length;
    window.parent.postMessage({ loggedInCount: loggedInEmployeesCount, totalEmployees: employees.length }, '*');
  }, [data, employees]);

  const fetchEntries = async (date) => {
    if (!date) {
      console.error('Date parameter is undefined');
      return;
    }
    setLoading(true);
    const formattedDate = formatDate(date);

    try {
      const response = await axiosInstance.get(`/admin/employeesAttendance?date=${formattedDate}`);
      // Process and normalize the timestamps
      const processedData = response.data.map(entry => ({
        ...entry,
        punch_in_time: normalizeDbTimestampToUtcIso(entry.punch_in_time, entry.attendance_date),
        punch_out_time: normalizeDbTimestampToUtcIso(entry.punch_out_time, entry.attendance_date),
      }));
      setData(processedData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/admin/employees');
      setEmployees(response.data);
    } catch (error) {
      if (!error.response) {
        toast.error("Looks like you are offline. Please check your internet connectivity.");
      } else {
        console.error(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const year = dateString.getFullYear();
    const month = String(dateString.getMonth() + 1).padStart(2, '0');
    const day = String(dateString.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const calculateWorkDuration = (startTime, endTime, breakTime) => {
    if (!startTime) {
      return "Employee Working...";
    } else if (!endTime) {
      return "Currently Working...";
    } else {
      const start = new Date(startTime);
      const end = new Date(endTime);
      const breakDuration = parseTimeToMinutes(breakTime);
      const workDuration = (end - start - breakDuration * 60 * 1000) / (60 * 1000);
      const hours = Math.floor(workDuration / 60);
      const minutes = Math.floor(workDuration % 60);
      return `${hours} hours ${minutes} minutes`;
    }
  };

  const parseTimeToMinutes = (timeString) => {
    if (!timeString) {
      return 0;
    }
    const [hours, minutes] = timeString.split(':').map(num => parseInt(num, 10));
    return hours * 60 + minutes;
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => setIsRefreshing(false), 30000);
    fetchEntries(selectedDate);
    fetchEmployees();
  };

  const handleRowClick = (employeeId, employeeName) => {
    navigate(`/projects-by-employee/${employeeId}`, {
      state: {
        employeeId,
        employeeName
      }
    });
  };

  // Format timezone for display
  const formatTimeZone = () => {
    try {
      const timeZoneAbbr = new Intl.DateTimeFormat('en', {
        timeZoneName: 'short',
        timeZone: userTimeZone
      }).formatToParts().find(part => part.type === 'timeZoneName')?.value;

      return timeZoneAbbr || userTimeZone.split('/').pop();
    } catch (e) {
      return userTimeZone.split('/').pop();
    }
  };

  return (
    <div className="employee-table-container">
      <h2 className='employee-table-title'>{t("attendanceTittle")}</h2>
      <div className="employee-table-datepicker-container">
        <div>
          <label htmlFor="selectedDate" style={{ fontSize: '12px', paddingLeft: '3px' }}>{t("attendance_dates")}</label>
          <input type="date" className='selectDate' id="selectedDate" value={selectedDate.toISOString().split('T')[0]} onChange={(e) => setSelectedDate(new Date(e.target.value))} />
        </div>
        <div>
          <button className="employee-table-refresh-button" onClick={handleRefresh} disabled={isRefreshing}>{t("attendance_refresh")}</button>
        </div>
        {/* <div className="timezone-display">
          (HH:MM AM/PM {formatTimeZone()})
        </div> */}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className='employee-table'>
          <thead>
            <tr>
              <th>{t("ID")}</th>
              <th>{t("attendance_name")}</th>
              <th>{t("attendance_login")}</th>
              <th>{t("attendance_logout")}</th>
              <th>{t("attendance_duration")}</th>
            </tr>
          </thead>
          <tbody style={{ background: '#fff' }}>
            {loading ? (
              <tr>
                <td className='table-data-row'></td>
                <td className='table-data-row'></td>
                <td className="employee-table-Spinner centered">
                  <Spinner animation="border" role="status">
                    <span className=""></span>
                  </Spinner>
                  <span className="mt-2">{t('Please wait your data is loading')}</span>
                </td>
                <td className='table-data-row'></td>
                <td className='table-data-row'></td>
              </tr>
            ) : employees.length === 0 ? (
              <tr>
                <td className='table-data-row'></td>
                <td className='table-data-row'></td>
                <td className="table-data-row centered">
                  <Alert variant="light" className='employee-table-Alert'>
                    {t("No employees found")}
                  </Alert>
                </td>
                <td className='table-data-row'></td>
                <td className='table-data-row'></td>
              </tr>
            ) : (
              employees
                .filter(employee => empRole === 'SuperAdmin' || adminIds.includes(employee.emp_id))
                .map(employee => {
                  const employeeAttendance = data.find(
                    attendance => attendance.emp_id === employee.emp_id &&
                      new Date(attendance.attendance_date).toDateString() === selectedDate.toDateString()
                  );

                  const isAbsent = !employeeAttendance || employeeAttendance.attendance === 'a';

                  return (
                    <tr key={employee.emp_id} onClick={() => handleRowClick(employee.emp_id, employee.p_name)} style={{ cursor: 'pointer' }}>
                      <td>{employee.emp_sap_id}</td>
                      <td>{employee.p_name}</td>
                      <td>
                        {isAbsent ? '' : formatISOToLocalTime(employeeAttendance.punch_in_time)}
                      </td>
                      <td>
                        {isAbsent ? t("attendance_absent") : (
                          employeeAttendance.punch_out_time ?
                            formatISOToLocalTime(employeeAttendance.punch_out_time) :
                            t("attendance_currentlyWorking")
                        )}
                      </td>
                      <td>
                        {isAbsent ? "" : (
                          employeeAttendance.punch_out_time ?
                            calculateWorkDuration(employeeAttendance.punch_in_time, employeeAttendance.punch_out_time, employeeAttendance.break_time) :
                            ""
                        )}
                      </td>
                    </tr>
                  );
                })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Table;